import type { TacticsPlayerSpot } from './types';

/**
 * Team compactness metrics — how stretched a team is along the pitch
 * length (vertical) and pitch width (horizontal).
 *
 * Coaches quote these constantly: a "compact 4-4-2" reads as low
 * vertical + low horizontal compactness; a "stretched 4-3-3" reads as
 * high horizontal and moderate vertical. Excluding the goalkeeper
 * keeps the metric meaningful — the GK alone in the back band would
 * dominate the vertical extent.
 *
 * Vertical = max(z) − min(z) over outfielders.
 * Horizontal = max(x) − min(x) over outfielders.
 * Units: metres.
 *
 * Returns null when fewer than 2 outfielders exist (a single point
 * can't define an extent). Goalkeeper detection: by role label
 * ("GK" exact match, case-insensitive). For custom roles that don't
 * use the canonical "GK" tag, the GK won't be excluded — a follow-up
 * could add a more permissive detector if it becomes a real issue.
 */

export interface TeamCompactness {
  /** Front-to-back stretch in metres. */
  vertical: number;
  /** Left-to-right spread in metres. */
  horizontal: number;
}

function isGoalkeeper(spot: TacticsPlayerSpot): boolean {
  return typeof spot.role === 'string' && spot.role.toUpperCase() === 'GK';
}

export function computeTeamCompactness(
  spots: ReadonlyArray<TacticsPlayerSpot>,
): TeamCompactness | null {
  if (!spots || spots.length === 0) return null;
  const outfielders = spots.filter((s) => !isGoalkeeper(s));
  if (outfielders.length < 2) return null;
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const s of outfielders) {
    if (s.x < minX) minX = s.x;
    if (s.x > maxX) maxX = s.x;
    if (s.z < minZ) minZ = s.z;
    if (s.z > maxZ) maxZ = s.z;
  }
  return {
    vertical: maxZ - minZ,
    horizontal: maxX - minX,
  };
}
